import React, { useEffect, useRef, useState, useCallback } from "react";
import { createMeeting, getToken, validateMeeting } from "../../api";
import ConfirmBox from "../ConfirmBox";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import {
    Mic,
    MicOff,
    Video,
    VideoOff,
    Volume2,
    ChevronDown,
    Sparkles,
    ArrowRight,
    Copy,
    Check,
} from "lucide-react";

import { Constants, useMediaDevice, createCameraVideoTrack, createMicrophoneAudioTrack } from "../../config/sdkConfig";
import { closeAllPreviewTracks } from "../../hooks/useAgoraStubs";

import useMediaStream from "../../hooks/useMediaStream";
import useIsMobile from "../../hooks/useIsMobile";
import useMobilePlatform from "../../hooks/useMobilePlatform";
import RLinkLogo from "../common/RLinkLogo";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import test_sound from '../../sounds/test_sound.wav'
import { VirtualBackgroundProcessor, VideoSDKNoiseSuppressor } from "@videosdk.live/videosdk-media-processor-web";
import { backgroundImageUrls } from "../../utils/background";
import {
    getVirtualBackgroundPreference,
    saveVirtualBackgroundPreference,
    getRoomVirtualBackgrounds,
    uploadVirtualBackground,
    deleteVirtualBackground,
} from "../../services/virtualBackgroundServices";
import { Image as ImageIcon, Loader2, Upload, X } from "lucide-react";
import novirtualbackground from '../../images/novirtualbackground.png';
import { useSiteContext } from "../../context/SiteContext";
import { unlockIOSAudio } from "../../utils/iosAudioUnlock";
import { useWhiteLabel } from "../../context/WhiteLabelContext";
import { useNavigate } from "react-router-dom";
import * as meetingServices from "../../services/meetingServices";
import { useBrandKit } from "../../context/BrandKitContext";
import WaitingForApproval from "./WaitingForApproval";

export function JoiningScreenNew({
    participantName,
    setParticipantName,
    setMeetingId,
    meetingId,
    setToken,
    setMicOn,
    setWebcamOn,
    onClickStartMeeting,
    customAudioStream,
    setCustomAudioStream,
    setCustomVideoStream,
    micOn,
    webcamOn,
    joiningToken,
    setJoiningToken,
}) {
    const { isAuthenticated, user, setMeetingRole, meetingRole, setMeetingSettings, meetingSettings, customMeetingName, setCustomMeetingName } = useSiteContext();
    const navigate = useNavigate();
    const { activeBrandKit } = useBrandKit();
    const bkColors = activeBrandKit?.colors || {};
    const bkPrimary = bkColors.primary || '#6a1fbf';
    const bkAccent = bkColors.accent || '#00c853';
    const bkBackground = bkColors.background || '#001233';

    // Detect recorder mode from URL params (persist in sessionStorage)
    const urlParams = new URLSearchParams(window.location.search);
    const isRecorderMode = urlParams.get('recorder') === 'true' || sessionStorage.getItem('isRecorderMode') === 'true';
    if (isRecorderMode) sessionStorage.setItem('isRecorderMode', 'true');
    const videoProcessorRef = useRef(null);
    if (!videoProcessorRef.current) {
        videoProcessorRef.current = new VirtualBackgroundProcessor();
    }
    const videoProcessor = videoProcessorRef.current;

    const {
        selectedWebcam,
        selectedMic,
        setSelectedMic,
        setSelectedWebcam,
        setSelectedSpeaker,
        selectedSpeaker,
        isCameraPermissionAllowed,
        isMicrophonePermissionAllowed,
        setIsCameraPermissionAllowed,
        setIsMicrophonePermissionAllowed,
    } = useMeetingAppContext();

    const { branding: wlBranding } = useWhiteLabel();
    const isMobile = useIsMobile();
    const { isIOS } = useMobilePlatform();
    const [roomName, setRoomName] = useState(null);
    const [userName, setUserName] = useState(participantName);
    const [selectedRole, setSelectedRole] = useState("Participant");
    const [dlgMuted, setDlgMuted] = useState(false);
    const [dlgDevices, setDlgDevices] = useState(false);
    const [didDeviceChange, setDidDeviceChange] = useState(false);
    const [testSpeaker, setTestSpeaker] = useState(false);
    const [showVideoOption, setShowVideoOption] = useState(false);
    const [showAudioOption, setShowAudioOption] = useState(false);
    const [showSpeakerOption, setShowSpeakerOption] = useState(false);
    const [showMicTest, setShowMicTest] = useState(false);
    const [showVirtualBackgroundModal, setShowVirtualBackgroundModal] = useState(false);
    const [selectedBackground, setSelectedBackground] = useState(null);
    const [passwordDigits, setPasswordDigits] = useState(['', '', '', '']);
    const [passwordError, setPasswordError] = useState(false);
    const [nameError, setNameError] = useState(false);
    const passwordInputRefs = useRef([]);

    const getRoomUrlSlug = (room) => {
        const p = room?.roomPermalink;
        const raw = (!p || p === "false" || p === "true") ? room?.roomName : p;
        return (raw || "").replace(/\s+/g, "-");
    };

    // Update the static apple-itunes-app meta tag with the current meeting URL
    useEffect(() => {
        if (!isIOS || isRecorderMode) return;
        const meta = document.querySelector('meta[name="apple-itunes-app"]');
        if (meta) {
            meta.setAttribute("content", `app-id=6553996625, app-argument=${window.location.href}`);
        }
    }, [isIOS, isRecorderMode]);

    // Mic test functionality states
    const [audioProgress, setAudioProgress] = useState(0);
    const [recordingProgress, setRecordingProgress] = useState(0);
    const [recordingStatus, setRecordingStatus] = useState("inactive");
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [volume, setVolume] = useState(null);
    const [audio, setAudio] = useState(null);
    const [{ webcams, mics, speakers }, setDevices] = useState({
        webcams: [],
        mics: [],
        speakers: [],
    });

    // Waiting room states
    const [isInWaitingRoom, setIsInWaitingRoom] = useState(false);
    const [wasRejected, setWasRejected] = useState(false);
    const [rejectionReason, setRejectionReason] = useState(null);
    const [hostIsOnline, setHostIsOnline] = useState(false);
    const waitingRoomSocketRef = useRef(null);

    // Duplicate session states
    const [showDuplicateSessionModal, setShowDuplicateSessionModal] = useState(false);
    const duplicateSessionDataRef = useRef(null);
    const pendingJoinCallbackRef = useRef(null);

    const { getVideoTrack, getAudioTrack, switchWebcam: switchWebcamDevice, switchMic: switchMicDevice } = useMediaStream();
    const noiseProcessorRef = useRef(null);
    const {
        checkPermissions,
        getCameras,
        getMicrophones,
        requestPermission,
        getPlaybackDevices,
    } = useMediaDevice({ onDeviceChanged });

    const [audioTrack, setAudioTrack] = useState(null);
    const [videoTrack, setVideoTrack] = useState(null);

    const videoPlayerRef = useRef();
    const audioPlayerRef = useRef();
    const videoTrackRef = useRef();
    const audioTrackRef = useRef();
    const audioAnalyserIntervalRef = useRef();
    const permissonAvaialble = useRef();
    const webcamRef = useRef();
    const micRef = useRef();

    // Mic test functionality refs
    const intervalRef = useRef();
    const mediaRecorder = useRef(null);
    const [showVirtualBackground, setShowVirtualBackground] = useState(false);
    const [copiedRoomName, setCopiedRoomName] = useState(false)
    const [roomBackgrounds, setRoomBackgrounds] = useState([]);
    const [isUploadingBg, setIsUploadingBg] = useState(false);
    const [isDeletingBgId, setIsDeletingBgId] = useState(null);
    const customBgInputRef = useRef(null);

    useEffect(() => {
        webcamRef.current = webcamOn;
    }, [webcamOn]);

    useEffect(() => {
        micRef.current = micOn;
        setRecordingStatus("recording");
    }, [micOn]);

    useEffect(() => {
        permissonAvaialble.current = {
            isCameraPermissionAllowed,
            isMicrophonePermissionAllowed,
        };
    }, [isCameraPermissionAllowed, isMicrophonePermissionAllowed]);

    useEffect(() => {
        if (micOn) {
            audioTrackRef.current = audioTrack;
            startMuteListener();
        } else {
            setVolume(0);
        }
    }, [micOn, audioTrack]);

    useEffect(() => {
        if (micOn) {
            if (audioTrackRef.current && audioTrackRef.current !== audioTrack) {
                audioTrackRef.current.stop();
            }

            audioTrackRef.current = audioTrack;

            if (audioTrack) {
                const audioSrcObject = new MediaStream([audioTrack]);
                if (audioPlayerRef.current) {
                    audioPlayerRef.current.srcObject = audioSrcObject;
                    audioPlayerRef.current
                        .play()
                        .catch((error) => console.log("audio play error", error));
                }
            } else {
                if (audioPlayerRef.current) {
                    audioPlayerRef.current.srcObject = null;
                }
            }
        }
    }, [micOn, audioTrack]);

    useEffect(() => {
        if (webcamOn) {
            if (videoTrackRef.current && videoTrackRef.current !== videoTrack) {
                videoTrackRef.current.stop();
            }

            videoTrackRef.current = videoTrack;

            var isPlaying =
                videoPlayerRef.current.currentTime > 0 &&
                !videoPlayerRef.current.paused &&
                !videoPlayerRef.current.ended &&
                videoPlayerRef.current.readyState >
                videoPlayerRef.current.HAVE_CURRENT_DATA;

            if (videoTrack) {
                const videoSrcObject = new MediaStream([videoTrack]);

                if (videoPlayerRef.current) {
                    videoPlayerRef.current.srcObject = videoSrcObject;
                    if (videoPlayerRef.current.pause && !isPlaying) {
                        videoPlayerRef.current
                            .play()
                            .catch((error) => console.log("error", error));
                    }
                }
            } else {
                if (videoPlayerRef.current) {
                    videoPlayerRef.current.srcObject = null;
                }
            }
        }
    }, [webcamOn, videoTrack]);

    const isWebinarParticipant = meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "host", "speaker"].includes(meetingRole);
    // Also skip device enumeration when muteParticipantsOnEntry is on for non-hosts
    const shouldSkipMedia = isWebinarParticipant ||
        (meetingSettings?.muteParticipantsOnEntry && !["moderator", "co-host", "host", "speaker"].includes(meetingRole));

    useEffect(() => {
        if (!shouldSkipMedia && meetingSettings?.roomType) getCameraDevices();
    }, [isCameraPermissionAllowed, shouldSkipMedia, meetingSettings?.roomType]);

    useEffect(() => {
        if (!shouldSkipMedia && meetingSettings?.roomType) getAudioDevices();
    }, [isMicrophonePermissionAllowed, shouldSkipMedia, meetingSettings?.roomType]);

    // Recorder mode: skip everything and auto-join immediately
    useEffect(() => {
        if (isRecorderMode) {
            setMicOn(false);
            setWebcamOn(false);
            setParticipantName('R-Link Recorder');
            // Small delay to ensure meeting settings are loaded
            const timer = setTimeout(() => {
                onClickStartMeeting();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isRecorderMode]);

    // Detect breakout redirect — JoiningScreenNew briefly renders before auto-start kicks in.
    // We must NOT run checkMediaPermission() in this case, as it would override the
    // mic/cam state that Conference.js carefully restored from localStorage.
    const isFromBreakoutRedirect = new URLSearchParams(window.location.search).get('fromBreakout') === '1';

    useEffect(() => {
        if (isRecorderMode) return; // Skip for recorder
        if (isFromBreakoutRedirect) return; // Skip for breakout — mic/cam already set by Conference.js
        // Wait for meetingSettings to load before requesting media permissions
        if (!meetingSettings?.roomType) return;

        const isHostOrSpeaker = ["moderator", "co-host", "host", "speaker"].includes(meetingRole);

        // For webinars, skip media permissions entirely unless user is a host/speaker
        if (meetingSettings.roomType === "Webinar" && !isHostOrSpeaker) {
            setMicOn(false);
            setWebcamOn(false);
            return;
        }

        // If room has "Mute Participants on Entry" enabled, skip permissions for non-hosts
        if (meetingSettings.muteParticipantsOnEntry && !isHostOrSpeaker) {
            setMicOn(false);
            setWebcamOn(false);
            return;
        }

        checkMediaPermission();
        return () => {
            // Cleanup noise processor on unmount
            if (noiseProcessorRef.current) {
                try {
                    if (noiseProcessorRef.current.stopProcessing) {
                        noiseProcessorRef.current.stopProcessing();
                    }
                } catch (error) {
                    console.log("Error stopping noise processor on unmount:", error);
                }
                noiseProcessorRef.current = null;
            }

            // Stop MediaStreamTracks held in refs (raw browser tracks)
            if (videoTrackRef.current) {
                try { videoTrackRef.current.stop(); } catch (e) { /* ignore */ }
                videoTrackRef.current = null;
            }
            if (audioTrackRef.current) {
                try { audioTrackRef.current.stop(); } catch (e) { /* ignore */ }
                audioTrackRef.current = null;
            }

            // Close the underlying Agora tracks that own the hardware handles.
            // Without this, Chromium keeps the camera/mic indicator lit after
            // navigating away from the pre-launch check page.
            closeAllPreviewTracks();
            console.log('[JoiningScreenNew] Unmount: all preview tracks cleaned up');
        };
    }, [meetingSettings?.roomType, meetingRole]);

    // Disable media for webinar participants when settings load
    useEffect(() => {
        if (meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "speaker"].includes(meetingRole)) {
            // Turn off mic and camera if they're on
            if (micOn) {
                const audioTrack = audioTrackRef.current;
                if (audioTrack) {
                    audioTrack.stop();
                    setAudioTrack(null);
                    setCustomAudioStream(null);
                    setMicOn(false);
                }
            }
            if (webcamOn) {
                const videoTrack = videoTrackRef.current;
                if (videoTrack) {
                    videoTrack.stop();
                    setVideoTrack(null);
                    setCustomVideoStream(null);
                    setWebcamOn(false);
                }
            }
        }
    }, [meetingSettings, meetingRole]);

    // read params of url   
    useEffect(() => {
        // const pathname = window.location.pathname;
        // const roomName = pathname.split('/').pop();
        // checkValidMeeting(roomName);
        if (meetingSettings) {
            setRoomName(meetingSettings.roomName);
        }
    }, [isAuthenticated]);

    // Set default meeting name to room name for moderators
    useEffect(() => {
        if (
            meetingSettings &&
            (meetingSettings.roomType === "Meeting" || meetingSettings.roomType === "Webinar") &&
            ["moderator", "co-host", "host"].includes(meetingRole) &&
            (!customMeetingName || customMeetingName.trim() === "")
        ) {
            const defaultName = roomName || meetingSettings.roomName || meetingSettings.roomType;
            setCustomMeetingName(defaultName);
        }
    }, [meetingSettings, meetingRole, roomName, customMeetingName, setCustomMeetingName]);

    // Auto-join for hosts when skipPrelaunchCheck flag is set (from Meet Now button)
    const autoJoinAttempted = useRef(false);
    useEffect(() => {
        const skipPrelaunch = localStorage.getItem("skipPrelaunchCheck");

        // Debug logging
        console.log("Auto-join check:", {
            skipPrelaunch,
            meetingId,
            joiningToken: !!joiningToken,
            meetingRole,
            participantName,
            userName,
            autoJoinAttempted: autoJoinAttempted.current
        });

        // Get display name from various sources
        const displayName = participantName || userName || user?.name || localStorage.getItem("displayName") || "Host";

        if (
            skipPrelaunch === "true" &&
            meetingId &&
            joiningToken &&
            ["moderator", "co-host", "host", "speaker"].includes(meetingRole) &&
            !autoJoinAttempted.current
        ) {
            // Mark as attempted to prevent re-triggering
            autoJoinAttempted.current = true;
            // Clear the flag
            localStorage.removeItem("skipPrelaunchCheck");

            console.log("Auto-joining meeting for host with name:", displayName);

            // Auto-join the meeting
            const autoJoin = async () => {
                try {
                    const { getToken } = await import("../../api");
                    const token = await getToken();
                    setParticipantName(displayName);
                    setUserName(displayName);
                    localStorage.setItem("participantName", displayName);
                    setToken(token);
                    setMeetingId(meetingId);
                    onClickStartMeeting();
                } catch (error) {
                    console.error("Auto-join failed:", error);
                    autoJoinAttempted.current = false; // Allow retry on error
                }
            };
            autoJoin();
        }
    }, [meetingId, joiningToken, meetingRole, participantName, userName, user, setToken, setMeetingId, onClickStartMeeting, setParticipantName]);

    const checkValidMeeting = async (roomName) => {
        try {
            const response = await meetingServices.getMeetingByName(roomName);
            const meeting = response.meeting;
            if (meeting) {
                meeting.scheduledStartTime = meeting.scheduledStartTime.replace(".000Z", "");
                if (meetingRole === "moderator" || meetingRole === "co-host" || meetingRole === "speaker") {
                    setMeetingSettings({
                        ...meeting,
                        guestCanUnmute: true,
                    });
                } else {
                    setMeetingSettings(meeting);
                }
                setRoomName(meeting.roomName);
                setMeetingRole(meeting.role);
                setMeetingId(meeting.roomId);
                console.log("meetingRole", meeting.role);
                const allowGuest = localStorage.getItem("allowguest");
                if (!isAuthenticated && !allowGuest) {
                    localStorage.setItem("roomId", getRoomUrlSlug(meeting) || roomName);
                    navigate(`/login`);
                } else {
                    const jointokenresp = await meetingServices.getJoiningToken({ roomId: meeting.roomId });
                    if (jointokenresp.token) {
                        setJoiningToken(jointokenresp.token);
                    }
                    localStorage.removeItem("roomId");
                    localStorage.removeItem("allowguest");
                }
            } else {
                // toast("Meeting not found", {
                //     position: "top-right",
                //     autoClose: 5000,
                //     hideProgressBar: true,
                //     closeButton: false,
                //     pauseOnHover: true,
                // });
                navigate(`/meeting-not-found`);
            }
        } catch (error) {
            console.log("Error in checkValidMeeting", error);
        }
    }

    const _toggleWebcam = () => {
        // Prevent webinar participants from toggling camera
        if (meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "speaker"].includes(meetingRole)) {
            return;
        }

        const videoTrack = videoTrackRef.current;

        if (webcamOn) {
            if (videoTrack) {
                videoTrack.stop();
                setVideoTrack(null);
                setCustomVideoStream(null);
                setWebcamOn(false);
            }
        } else {
            getDefaultMediaTracks({ mic: false, webcam: true });
            setWebcamOn(true);
        }
    };

    const _toggleMic = async () => {
        // Prevent webinar participants from toggling microphone
        if (meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "speaker"].includes(meetingRole)) {
            return;
        }

        const audioTrack = audioTrackRef.current;

        if (micOn) {
            if (audioTrack) {
                audioTrack.stop();
                setAudioTrack(null);
                setCustomAudioStream(null);
                setMicOn(false);
                // Clean up noise processor when mic is turned off
                if (noiseProcessorRef.current) {
                    try {
                        if (noiseProcessorRef.current.stopProcessing) {
                            await noiseProcessorRef.current.stopProcessing();
                        }
                    } catch (error) {
                        // Ignore errors when stopping
                        console.log("Error stopping noise processor:", error);
                    }
                    noiseProcessorRef.current = null;
                }
            }
        } else {
            if (false) {
                // Create a new noise processor instance each time to avoid "processing has already started" error
                if (noiseProcessorRef.current) {
                    try {
                        // Try to stop existing processing if possible
                        if (noiseProcessorRef.current.stopProcessing) {
                            await noiseProcessorRef.current.stopProcessing();
                        }
                    } catch (error) {
                        // Ignore errors when stopping
                        console.log("Error stopping noise processor:", error);
                    }
                }
                noiseProcessorRef.current = new VideoSDKNoiseSuppressor();

                const stream = await createMicrophoneAudioTrack({});
                const processedStream = await noiseProcessorRef.current.getNoiseSuppressedAudioStream(
                    stream
                );

                // Set the processed stream directly instead of calling changeMic
                setCustomAudioStream(processedStream);
                const audioTracks = processedStream?.getAudioTracks();
                const newAudioTrack = audioTracks.length ? audioTracks[0] : null;
                clearInterval(audioAnalyserIntervalRef.current);
                setAudioTrack(newAudioTrack);
                setMicOn(true);
            } else {
                getDefaultMediaTracks({ mic: true, webcam: false });
                setMicOn(true);
            }
        }
    };

    const changeWebcam = async (deviceId) => {
        if (webcamOn) {
            // Prefer setDevice() on the existing Agora preview track — preserves
            // track identity so hardware handles aren't orphaned. Falls back to
            // creating a new track when no preview exists yet or setDevice throws.
            const stream = await switchWebcamDevice({ webcamId: deviceId });
            if (!stream) return;
            setCustomVideoStream(stream);
            const videoTracks = stream?.getVideoTracks?.();
            const videoTrack = videoTracks?.length ? videoTracks[0] : null;
            setVideoTrack(videoTrack);
        }
    };

    const changeMic = async (deviceId) => {
        if (micOn) {
            // See changeWebcam above for rationale.
            clearInterval(audioAnalyserIntervalRef.current);
            const stream = await switchMicDevice({ micId: deviceId });
            if (!stream) return;
            setCustomAudioStream(stream);
            const audioTracks = stream?.getAudioTracks?.();
            const audioTrack = audioTracks?.length ? audioTracks[0] : null;
            setAudioTrack(audioTrack);
        }
    };

    const getDefaultMediaTracks = async ({ mic, webcam }) => {
        // Never access media devices for webinar participants
        if (shouldSkipMedia) return;
        if (mic) {
            const stream = await getAudioTrack({
                micId: selectedMic.id,
            });
            setCustomAudioStream(stream);
            const audioTracks = stream?.getAudioTracks();
            const audioTrack = audioTracks?.length ? audioTracks[0] : null;
            setAudioTrack(audioTrack);
        }

        if (webcam) {
            const stream = await getVideoTrack({
                webcamId: selectedWebcam?.id,
            });
            setCustomVideoStream(stream);
            const videoTracks = stream?.getVideoTracks();
            const videoTrack = videoTracks?.length ? videoTracks[0] : null;
            setVideoTrack(videoTrack);
        }
    };

    async function startMuteListener() {
        const currentAudioTrack = audioTrackRef.current;
        if (currentAudioTrack) {
            if (currentAudioTrack.muted) {
                setDlgMuted(true);
            }
            currentAudioTrack.addEventListener("mute", (ev) => {
                setDlgMuted(true);
            });
        }
    }

    const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    async function requestAudioVideoPermission(mediaType) {
        // Never request permissions for webinar participants or when muteOnEntry is set
        if (shouldSkipMedia) return;
        try {
            const permission = await requestPermission(mediaType);

            if (isFirefox) {
                const isVideoAllowed = permission.get("video");
                setIsCameraPermissionAllowed(isVideoAllowed);
                if (isVideoAllowed) {
                    setWebcamOn(true);
                    await getDefaultMediaTracks({ mic: false, webcam: true });
                }
            }

            if (isFirefox) {
                const isAudioAllowed = permission.get("audio");
                setIsMicrophonePermissionAllowed(isAudioAllowed);
                if (isAudioAllowed) {
                    setMicOn(true);
                    await getDefaultMediaTracks({ mic: true, webcam: false });
                }
            }

            if (mediaType === Constants.permission.AUDIO) {
                const isAudioAllowed = permission.get(Constants.permission.AUDIO);
                setIsMicrophonePermissionAllowed(isAudioAllowed);
                if (isAudioAllowed) {
                    setMicOn(true);
                    await getDefaultMediaTracks({ mic: true, webcam: false });
                }
            }

            if (mediaType === Constants.permission.VIDEO) {
                const isVideoAllowed = permission.get(Constants.permission.VIDEO);
                setIsCameraPermissionAllowed(isVideoAllowed);
                if (isVideoAllowed) {
                    setWebcamOn(true);
                    await getDefaultMediaTracks({ mic: false, webcam: true });
                }
            }
        } catch (ex) {
            console.log("Error in requestPermission ", ex);
        }
    }

    function onDeviceChanged() {
        setDidDeviceChange(true);
        getCameraDevices();
        getAudioDevices();
        getDefaultMediaTracks({ mic: micRef.current, webcam: webcamRef.current });
    }

    const checkMediaPermission = async () => {
        // Skip media initialization entirely for webinar participants (non-moderators)
        const isWebinarAttendee = meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "host", "speaker"].includes(meetingRole);
        if (isWebinarAttendee) {
            console.log("Webinar participant - skipping all media initialization");
            setMicOn(false);
            setWebcamOn(false);
            return;
        }

        // Check room-level mute settings
        const roomMuteOnEntry = meetingSettings?.muteParticipantsOnEntry === true;
        const isParticipantRole = !["moderator", "co-host", "host"].includes(meetingRole);

        // Load user settings for muteOnJoin and cameraOffOnJoin
        let muteOnJoin = false;
        let cameraOffOnJoin = false;
        try {
            const savedSettings = localStorage.getItem('rlink_settings');
            if (savedSettings) {
                const settings = JSON.parse(savedSettings);
                muteOnJoin = settings.muteOnJoin === true;
                cameraOffOnJoin = settings.cameraOffOnJoin === true;
            }
        } catch (e) {
            console.log("Error loading settings:", e);
        }

        // Apply room-level mute for participants
        if (roomMuteOnEntry && isParticipantRole) {
            muteOnJoin = true;
        }

        try {
            const checkAudioVideoPermission = await checkPermissions();
            const cameraPermissionAllowed = checkAudioVideoPermission.get(
                Constants.permission.VIDEO
            );
            const microphonePermissionAllowed = checkAudioVideoPermission.get(
                Constants.permission.AUDIO
            );

            setIsCameraPermissionAllowed(cameraPermissionAllowed);
            setIsMicrophonePermissionAllowed(microphonePermissionAllowed);

            // Only turn on mic if permission is allowed AND muteOnJoin is NOT set
            if (microphonePermissionAllowed && !muteOnJoin) {
                setMicOn(true);
                getDefaultMediaTracks({ mic: true, webcam: false });
            } else if (!microphonePermissionAllowed) {
                await requestAudioVideoPermission(Constants.permission.AUDIO);
            }
            // Only turn on camera if permission is allowed AND cameraOffOnJoin is NOT set
            if (cameraPermissionAllowed && !cameraOffOnJoin) {
                setWebcamOn(true);
                getDefaultMediaTracks({ mic: false, webcam: true });
            } else if (!cameraPermissionAllowed) {
                await requestAudioVideoPermission(Constants.permission.VIDEO);
            }
        } catch (error) {
            await requestAudioVideoPermission();
            console.log(error);
        }
    };

    // Auto-pick the system-preferred device when the user has no saved selection.
    // Priority: "Primary" (some headsets) > "Default" (Windows) > "(Built-in)" (macOS) > first device.
    const PREFERRED_DEVICE_KEYWORDS = ['Primary', 'Default', '(Built-in)'];
    const pickPreferredDevice = (deviceList) => {
        for (const keyword of PREFERRED_DEVICE_KEYWORDS) {
            const match = deviceList.find(d => d.label?.includes(keyword));
            if (match) return match;
        }
        return deviceList[0];
    };

    const getCameraDevices = async () => {
        try {
            if (permissonAvaialble.current?.isCameraPermissionAllowed) {
                let webcams = await getCameras();
                const preferredWebcam = pickPreferredDevice(webcams);
                setSelectedWebcam({
                    id: preferredWebcam?.deviceId,
                    label: preferredWebcam?.label,
                });
                setDevices((devices) => {
                    return { ...devices, webcams };
                });
            }
        } catch (err) {
            console.log("Error in getting camera devices", err);
        }
    };

    const getAudioDevices = async () => {
        try {
            if (permissonAvaialble.current?.isMicrophonePermissionAllowed) {
                let mics = await getMicrophones();
                let speakers = await getPlaybackDevices();
                const hasMic = mics.length > 0;
                if (hasMic) {
                    startMuteListener();
                }

                const preferredMic = pickPreferredDevice(mics);
                const preferredSpeaker = pickPreferredDevice(speakers);

                setSelectedSpeaker({
                    id: preferredSpeaker?.deviceId,
                    label: preferredSpeaker?.label,
                });
                await setSelectedMic({ id: preferredMic?.deviceId, label: preferredMic?.label });
                setDevices((devices) => {
                    return { ...devices, mics, speakers };
                });
            }
        } catch (err) {
            console.log("Error in getting audio devices", err);
        }
    };

    useEffect(() => {
        getAudioDevices();
    }, []);

    // Mic test functionality useEffects
    useEffect(() => {
        if (audioTrack) {
            analyseAudio(audioTrack);
        } else {
            stopAudioAnalyse();
        }
    }, [audioTrack]);

    useEffect(() => {
        if (didDeviceChange) {
            setDidDeviceChange(false)
            if (mediaRecorder.current != null && mediaRecorder.current.state === "recording") {
                stopRecording()
            }
            setRecordingProgress(0)
            setRecordingStatus("inactive")
        }
    }, [didDeviceChange]);

    // Every non-host/non-cohost must go through the waiting-room socket flow.
    // The backend decides admit vs hold based on whether a host/cohost is
    // already connected and the meeting's `waitingRoom` setting. Routing
    // through the waiting-room socket is mandatory — bypassing it would
    // let an outside visitor hijack the room before the real host arrives.
    const isWaitingRoomRequired = useCallback(() => {
        const isHost = ["moderator", "co-host", "host"].includes(meetingRole);
        console.log('[WaitingRoom] Check:', {
            meetingRole,
            isHost,
            hostApprovalRequired: meetingSettings?.hostApprovalRequired,
            waitingRoom: meetingSettings?.waitingRoom,
            result: !isHost,
        });
        return !isHost;
    }, [meetingRole, meetingSettings]);

    // Connect to waiting room socket
    const connectToWaitingRoom = useCallback((participantId, participantName) => {
        const baseUrl = process.env.REACT_APP_BASE_URL?.replace('/api', '') || '';
        const socket = io(baseUrl, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        waitingRoomSocketRef.current = socket;

        socket.on('connect', () => {
            console.log('[WaitingRoom] Connected, joining waiting room...');
            // Send email so backend can verify if user is registered
            const webinarReg = localStorage.getItem(`webinar_reg_${meetingId}`);
            const regEmail = webinarReg ? JSON.parse(webinarReg)?.email : null;
            socket.emit('join-waiting-room', {
                meetingId,
                odId: participantId,
                odName: participantName,
                odAvatar: user?.logo,
                odEmail: user?.email || regEmail || null
            });
        });

        socket.on('admission-response', (data) => {
            console.log('[WaitingRoom] Admission response:', data);
            if (data.admitted) {
                // Clean up socket
                socket.disconnect();
                waitingRoomSocketRef.current = null;
                setIsInWaitingRoom(false);
                // Proceed to meeting
                onClickStartMeeting();
            } else {
                // Rejected
                setWasRejected(true);
                setRejectionReason(data.reason);
                socket.disconnect();
                waitingRoomSocketRef.current = null;
                // Redirect after countdown
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            }
        });

        socket.on('host-status', (data) => {
            console.log('[WaitingRoom] Host status:', data);
            setHostIsOnline(data.online);
        });

        // Host sent a message to this waiting participant
        socket.on('waiting-room-message', (data) => {
            console.log('[WaitingRoom] Message from host:', data);
            toast.info(
                `💬 ${data.senderName || 'Host'}: ${data.message}`,
                { position: 'top-center', autoClose: 10000, theme: 'dark' }
            );
        });

        // Broadcast message from host (sent to everyone including waiting room)
        socket.on('meeting-state:broadcast-message', (data) => {
            const { message, senderName } = data;
            if (!message) return;
            console.log('[WaitingRoom] Broadcast message received:', message);
            toast.info(
                `📢 ${senderName || 'Host'}: ${message}`,
                {
                    position: 'top-center',
                    autoClose: 10000,
                    theme: 'dark',
                    style: { background: '#1a1a2e', border: '1px solid rgba(106, 31, 191, 0.5)' },
                }
            );
        });

        socket.on('disconnect', () => {
            console.log('[WaitingRoom] Disconnected');
        });

        socket.on('connect_error', (error) => {
            console.error('[WaitingRoom] Connection error:', error.message);
            toast.error("Failed to connect to waiting room. Please try again.", {
                position: "top-right",
                autoClose: 3000,
            });
            setIsInWaitingRoom(false);
        });
    }, [meetingId, user, navigate, onClickStartMeeting]);

    // Leave waiting room
    const leaveWaitingRoom = useCallback(() => {
        if (waitingRoomSocketRef.current) {
            waitingRoomSocketRef.current.emit('leave-waiting-room', {
                meetingId,
                odId: user?.id || localStorage.getItem(meetingId)
            });
            waitingRoomSocketRef.current.disconnect();
            waitingRoomSocketRef.current = null;
        }
        setIsInWaitingRoom(false);
        setWasRejected(false);
        setRejectionReason(null);
    }, [meetingId, user]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (waitingRoomSocketRef.current) {
                waitingRoomSocketRef.current.disconnect();
            }
        };
    }, []);

    // Check for duplicate session and force-remove if user confirms
    const checkAndHandleDuplicateSession = async (proceedCallback) => {
        // Only check for logged-in users (guests don't have persistent userId)
        if (!isAuthenticated || !user?.id) {
            proceedCallback();
            return;
        }

        const meetingIdToCheck = meetingSettings?.roomId || meetingId;
        if (!meetingIdToCheck) {
            proceedCallback();
            return;
        }

        try {
            const baseUrl = process.env.REACT_APP_BASE_URL || '';
            const response = await fetch(`${baseUrl}/agora/check-duplicate-session/${meetingIdToCheck}/${user.id}`);
            const data = await response.json();

            if (data.isDuplicate) {
                // If the existing session is from the same browser tab (page refresh), skip the modal
                const localSessionId = sessionStorage.getItem('rlink_browser_session_id');
                if (localSessionId && data.existingBrowserSessionId === localSessionId) {
                    proceedCallback();
                    return;
                }

                // Different device/tab — show confirmation modal
                duplicateSessionDataRef.current = {
                    meetingId: meetingIdToCheck,
                    userId: user.id,
                    existingParticipantId: data.existingParticipantId,
                };
                pendingJoinCallbackRef.current = proceedCallback;
                setShowDuplicateSessionModal(true);
                return;
            }
        } catch (err) {
            console.warn('[DuplicateSession] Check failed, proceeding:', err);
        }

        proceedCallback();
    };

    const handleDuplicateSessionConfirm = async () => {
        setShowDuplicateSessionModal(false);
        const sessionData = duplicateSessionDataRef.current;
        if (!sessionData) return;

        try {
            const baseUrl = process.env.REACT_APP_BASE_URL || '';
            await fetch(`${baseUrl}/agora/force-remove-participant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    meetingId: sessionData.meetingId,
                    userId: sessionData.userId,
                    targetParticipantId: sessionData.existingParticipantId,
                }),
            });
        } catch (err) {
            console.warn('[DuplicateSession] Force-remove failed:', err);
        }

        // Small delay to let the old session disconnect cleanly
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Proceed with joining
        if (pendingJoinCallbackRef.current) {
            pendingJoinCallbackRef.current();
            pendingJoinCallbackRef.current = null;
        }
        duplicateSessionDataRef.current = null;
    };

    const handleDuplicateSessionCancel = () => {
        setShowDuplicateSessionModal(false);
        duplicateSessionDataRef.current = null;
        pendingJoinCallbackRef.current = null;
    };

    const handleLaunchRoom = async () => {
        // Unlock iOS audio pipeline on user gesture (tap/click)
        unlockIOSAudio();

        // Validate name is required
        if (!userName || userName.trim() === '') {
            setNameError(true);
            toast.error("Please enter your name to join the meeting", {
                toastId: 'join-name-required',
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: true,
                closeButton: false,
            });
            return;
        }

        // Clear name error if name is valid
        if (nameError) {
            setNameError(false);
        }

        // Log the password
        const password = passwordDigits.join('');
        // console.log('Password entered:', password);
        setParticipantName(userName);
        localStorage.setItem("participantName", userName);
        const token = await getToken();
        // const { meetingId, err } = await createMeeting({ token });
        // console.log("err", err);
        if (meetingId) {
            if (meetingSettings && meetingSettings.requirePassword && password !== meetingSettings.password) {
                setPasswordError(true);
                return;
            }
            setToken(token);
            setMeetingId(meetingId);

            // For webinars, disable audio and video for non-moderators
            if (meetingSettings && meetingSettings.roomType === "Webinar" && !["moderator", "co-host", "speaker"].includes(meetingRole)) {
                if (micOn) {
                    _toggleMic();
                }
                if (webcamOn) {
                    _toggleWebcam();
                }
            }

            // Mute participants if setting is enabled
            if (!["moderator", "co-host", "speaker"].includes(meetingRole) && meetingSettings && meetingSettings.muteParticipants && micOn) {
                _toggleMic();
            }
            if (localStorage.getItem("lowres")) {
                const stream = await createCameraVideoTrack({
                    optimizationMode: "motion",
                    encoderConfig: "h1080p_w1920p",
                    facingMode: "environment",
                    multiStream: false,
                });
                setCustomVideoStream(stream);
                changeWebcam(stream.id);
                localStorage.removeItem("lowres");
            }

            // Refresh meeting settings so the rest of the page reflects them.
            // We no longer use the meeting's `waitingRoom` flag to decide
            // whether to route through the waiting-room socket — every
            // non-host/non-cohost goes through the waiting-room flow and the
            // backend decides admit vs hold based on host presence.
            try {
                const roomSlug = window.location.pathname.split('/').filter(Boolean).pop();
                if (roomSlug) {
                    const freshData = await meetingServices.getMeetingByName(roomSlug);
                    if (freshData?.meeting) {
                        setMeetingSettings(freshData.meeting);
                    }
                }
            } catch (err) {
                console.warn('[WaitingRoom] Failed to re-fetch meeting settings, using cached:', err);
            }

            // Only host and cohost bypass the waiting-room socket. Everyone
            // else — guests, speakers, registered users, even auto-admit
            // candidates — must go through the backend gate so it can hold
            // them when no host is yet present.
            const isHostOrCohost = ["moderator", "co-host", "host"].includes(meetingRole);
            if (!isHostOrCohost) {
                const participantId = user?.id || localStorage.getItem(meetingId);
                setIsInWaitingRoom(true);
                connectToWaitingRoom(participantId, userName);
            } else {
                // Host/cohost - check for duplicate session, then proceed
                checkAndHandleDuplicateSession(() => onClickStartMeeting());
            }
        } else {
            // toast(`${err}`, {
            //     position: "bottom-left",
            //     autoClose: 4000,
            //     hideProgressBar: true,
            //     closeButton: false,
            //     pauseOnHover: true,
            //     draggable: true,
            //     progress: undefined,
            //     theme: "light",
            // });
        }
    };

    const copyRoomName = () => {
        navigator.clipboard.writeText(window.location.href);
        toast("Room URL copied to clipboard", {
            position: "bottom-left",
            autoClose: 2000,
            hideProgressBar: true,
            closeButton: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
        setCopiedRoomName(true);
        setTimeout(() => {
            setCopiedRoomName(false);
        }, 2000);
    };

    // Mic test functionality
    const mimeType = "audio/webm";

    const analyseAudio = (audioTrack) => {
        if (!micOn) return;
        const audioStream = new MediaStream([audioTrack]);
        const audioContext = new AudioContext();
        const audioSource = audioContext.createMediaStreamSource(audioStream);
        const analyser = audioContext.createAnalyser();

        analyser.fftSize = 512;
        analyser.minDecibels = -127;
        analyser.maxDecibels = 0;
        analyser.smoothingTimeConstant = 0.4;

        audioSource.connect(analyser);

        const volumes = new Uint8Array(analyser.frequencyBinCount);
        const volumeCallback = () => {
            analyser.getByteFrequencyData(volumes);
            const volumeSum = volumes.reduce((sum, vol) => sum + vol);
            const averageVolume = volumeSum / volumes.length;
            // make it smoother
            setVolume(averageVolume / 18.6666);
        };
        audioAnalyserIntervalRef.current = setInterval(volumeCallback, 500);
    };

    const stopAudioAnalyse = () => {
        clearInterval(audioAnalyserIntervalRef.current);
    };

    const handlePlaying = () => {
        setRecordingStatus("playing");
        const audioTags = document.getElementsByTagName("audio");

        for (let i = 0; i < audioTags.length; i++) {
            audioTags.item(i).setSinkId(selectedSpeaker?.id).then(() => {
                audioTags.item(i).play();
                audioTags.item(i).addEventListener('timeupdate', () => {
                    const progress = (audioTags.item(i).currentTime / recordingDuration) * 100;
                    setAudioProgress(progress);
                });
                audioTags.item(i).addEventListener('ended', () => {
                    setAudioProgress(0);
                });
            })
        }
    };

    const startRecording = async () => {
        setRecordingStatus("recording");

        try {
            const media = new MediaRecorder(customAudioStream, { type: mimeType });
            mediaRecorder.current = media;
            mediaRecorder.current.start();
            let localAudioChunks = [];

            mediaRecorder.current.ondataavailable = (event) => {
                if (typeof event.data === "undefined") return;
                if (event.data.size === 0) return;
                localAudioChunks.push(event.data);
            };

            mediaRecorder.current.onstop = () => {
                const audioBlob = new Blob(localAudioChunks, { type: mimeType });
                const audioUrl = URL.createObjectURL(audioBlob);
                setAudio(audioUrl);
                localAudioChunks = []
                const elapsedTime = Date.now() - startTime;
                const durationInSeconds = (elapsedTime / 1000);
                setRecordingDuration(durationInSeconds)
            };

            const startTime = Date.now();
            intervalRef.current = setInterval(() => {
                const elapsedTime = Date.now() - startTime;
                const progress = (elapsedTime / 7000) * 100;
                setRecordingProgress(progress);
            });

            setTimeout(() => {
                clearInterval(intervalRef.current)
                stopRecording();
            }, 7000)
        } catch (err) {
            console.log("Error in MediaRecorder:", err)
        }
    };

    const stopRecording = () => {
        if (mediaRecorder.current && mediaRecorder.current.state != "inactive") {
            setRecordingProgress(0);
            setRecordingStatus("stopped recording");
            clearInterval(intervalRef.current)
            mediaRecorder.current.stop();
        }
    };

    const testingSpeaker = () => {
        setTestSpeaker(!testSpeaker)
    };

    // Placeholder components for missing icons
    const MicIcon = ({ isOn }) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" width="28" height="28" style={{ color: isOn ? 'rgb(30, 217, 96)' : 'red' }}>
            <path d="M202.57,125.6v38.26c0,29.32-23.86,53.18-53.18,53.18l.03.11c-28.77-.64-52-24.23-52-53.15v-38.26h-14.16v38.26c0,35.62,27.79,64.84,62.83,67.17v44.91h-29.45v14.16h71.11v-14.16h-27.5v-45.52l-.17-.24c32.07-5.14,56.65-32.97,56.65-66.47v-38.26h-14.16Z" fill="currentColor"></path>
            <path d="M150.61,198.57c20.43,0,37.15-16.71,37.15-37.15V46.91c0-20.43-16.71-37.15-37.15-37.15s-37.15,16.71-37.15,37.15v114.52c0,20.43,16.71,37.15,37.15,37.15Z" fill="currentColor"></path>
        </svg>
    );

    const CameraIcon = ({ isOn }) => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" width="28" height="28" style={{ color: isOn ? 'rgb(30, 217, 96)' : 'red' }}>
            <path d="M70.66,34.48v-.96c0-2.08-.55-4.04-1.51-5.73l-44.17,44.17c.48.06.97.1,1.47.1h32.57c6.43,0,11.63-5.21,11.63-11.63v-1.42l15.74,7.64V27.2l-15.74,7.27Z" fill="currentColor"></path>
            <path d="M74.1,18.37l-1.51-1.51-2.23-2.23-7.8,7.8c-1.12-.36-2.3-.55-3.54-.55H26.45c-6.43,0-11.63,5.21-11.63,11.63v26.9c0,2.73.95,5.25,2.53,7.23l-7.91,7.91,3.74,3.74,1.57-1.57,6.77-6.77,45.7-45.7,6.88-6.88Z" fill="currentColor"></path>
        </svg>
    );

    const ScreenShareIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 512 512" style={{ color: 'red' }}>
            <path d="M0 0 C6.52984143 5.30549616 9.92921656 11.40483717 12.5625 19.3046875 C13.35381511 31.14397889 12.32590663 39.96176853 4.6484375 49.2265625 C-2.13629399 55.98588301 -10.33833999 59.2023819 -19.8125 59.6171875 C-29.76278878 59.32604308 -37.17251403 55.49804101 -44.16015625 48.60546875 C-50.459218 41.71828822 -52.80207104 34.02582132 -52.6484375 24.76171875 C-51.86413535 15.6260511 -48.03599328 7.28094242 -41.0703125 1.2578125 C-28.6795555 -7.85636134 -12.85904709 -8.94363211 0 0 Z " fill="currentColor" transform="translate(318.4375,186.6953125)"></path>
        </svg>
    );

    const CameraOffIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" width="32" height="32" className="text-white">
            <path d="M70.66,34.48v-.96c0-2.08-.55-4.04-1.51-5.73l-44.17,44.17c.48.06.97.1,1.47.1h32.57c6.43,0,11.63-5.21,11.63-11.63v-1.42l15.74,7.64V27.2l-15.74,7.27Z" fill="currentColor"></path>
            <path d="M74.1,18.37l-1.51-1.51-2.23-2.23-7.8,7.8c-1.12-.36-2.3-.55-3.54-.55H26.45c-6.43,0-11.63,5.21-11.63,11.63v26.9c0,2.73.95,5.25,2.53,7.23l-7.91,7.91,3.74,3.74,1.57-1.57,6.77-6.77,45.7-45.7,6.88-6.88Z" fill="currentColor"></path>
        </svg>
    );

    const CopyIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none">
            <path d="M16 12.9V17.1C16 20.6 14.6 22 11.1 22H6.9C3.4 22 2 20.6 2 17.1V12.9C2 9.4 3.4 8 6.9 8H11.1C14.6 8 16 9.4 16 12.9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M22 6.9V11.1C22 14.6 20.6 16 17.1 16H16V12.9C16 9.4 14.6 8 11.1 8H8V6.9C8 3.4 9.4 2 12.9 2H17.1C20.6 2 22 3.4 22 6.9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.4"></path>
        </svg>
    );

    const PersonIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm-7 7a7 7 0 0 1 14 0 1 1 0 0 1-1 1H6a1 1 0 0 1-1-1Z"></path>
        </svg>
    );

    const testSpeakers = () => {
        const selectedSpeakerDeviceId = selectedSpeaker.id
        if (selectedSpeakerDeviceId) {
            const audio = new Audio(test_sound);
            try {
                audio.setSinkId(selectedSpeakerDeviceId)
                    .then(() => {
                        audio.play();
                        // setIsPlaying(true)
                        // audio.addEventListener('timeupdate', () => {
                        //     const progress = (audio.currentTime / audio.duration) * 100;
                        //     setAudioProgress(progress);
                        // });
                        // audio.addEventListener('ended', () => {

                        // });
                    })
            } catch (error) {
                console.log(error);
            };
            audio.play().catch(error => {
                console.error('Failed to set sinkId:', error);
            });
        } else {
            console.error('Selected speaker deviceId not found.');
        }
    };

    const selectedBackgroundInitRef = useRef(true);
    useEffect(() => {
        // Skip the initial mount — this effect is only for when the user changes their background selection.
        // On mount, checkMediaPermission() handles camera initialization and running on mount
        // causes a race condition where two createCameraVideoTrack calls compete for the device.
        if (selectedBackgroundInitRef.current) {
            selectedBackgroundInitRef.current = false;
            return;
        }
        if (selectedBackground) {
            handleStartVirtualBackground();
        } else {
            changeWebcam(selectedWebcam.id);
        }
    }, [selectedBackground]);

    // Persist VB preference so MeetingContainerNew auto-applies it on join
    const persistPrelaunchVbPreference = (pref) => {
        try {
            const roomId = meetingSettings?.roomId;
            if (!roomId) return;
            saveVirtualBackgroundPreference(roomId, pref).catch((e) =>
                console.warn("Failed to save VB preference from pre-launch:", e?.message || e)
            );
        } catch (e) {
            console.warn("persistPrelaunchVbPreference error:", e);
        }
    };

    // Fetch user's uploaded backgrounds when modal first opens
    useEffect(() => {
        if (!showVirtualBackgroundModal) return;
        let cancelled = false;
        getRoomVirtualBackgrounds()
            .then((res) => {
                if (cancelled) return;
                const backgrounds = res?.data?.data || res?.data;
                if (Array.isArray(backgrounds)) setRoomBackgrounds(backgrounds);
            })
            .catch((err) => console.warn("[VirtualBG] Fetch failed:", err?.message || err));
        return () => { cancelled = true; };
    }, [showVirtualBackgroundModal]);

    // Load saved VB preference once meeting settings are available
    const vbPrefLoadedRef = useRef(false);
    useEffect(() => {
        const roomId = meetingSettings?.roomId;
        if (!roomId || vbPrefLoadedRef.current) return;
        vbPrefLoadedRef.current = true;
        (async () => {
            try {
                const res = await getVirtualBackgroundPreference(roomId);
                const pref = res?.data?.data ?? res?.data ?? null;
                if (pref?.type === 'image' && pref.imageUrl) {
                    selectedBackgroundInitRef.current = false;
                    setSelectedBackground(pref.imageUrl);
                    setShowVirtualBackground(true);
                }
            } catch (err) {
                // No saved preference is normal
            }
        })();
    }, [meetingSettings?.roomId]);

    // Re-apply VB to preview when webcam is turned on after a background was already chosen.
    // NOTE: Do NOT add selectedBackground to deps — there is a separate effect
    // above (deps: [selectedBackground]) that already handles background changes.
    // Duplicating here causes a race: both effects fire at once, both open the
    // camera + processor, state corrupts, background flashes then vanishes.
    useEffect(() => {
        if (webcamOn && selectedBackground && !selectedBackgroundInitRef.current) {
            handleStartVirtualBackground();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webcamOn]);

    const handleSelectPrelaunchBackground = (bgUrl) => {
        if (bgUrl) {
            selectedBackgroundInitRef.current = false;
            setSelectedBackground(bgUrl);
            setShowVirtualBackground(true);
            persistPrelaunchVbPreference({ type: 'image', imageUrl: bgUrl });
            // Effect B (deps: [webcamOn, selectedBackground]) will pick up the
            // new selectedBackground on the next render and call
            // handleStartVirtualBackground() with the updated closure value.
            // Do NOT call it directly here — the closure would still hold the
            // OLD selectedBackground and apply the wrong background first.
        } else {
            setSelectedBackground(null);
            setShowVirtualBackground(false);
            persistPrelaunchVbPreference({ type: 'none' });
        }
        setShowVirtualBackgroundModal(false);
    };

    const handlePrelaunchBgUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const roomId = meetingSettings?.roomId;
        if (!roomId) return;
        setIsUploadingBg(true);
        try {
            const res = await uploadVirtualBackground(file, roomId);
            const bg = res?.data?.data || res?.data;
            if (bg?._id && bg?.url) {
                setRoomBackgrounds((prev) => [bg, ...prev]);
                handleSelectPrelaunchBackground(bg.url);
            }
        } catch (err) {
            console.warn("[VirtualBG] Upload failed:", err?.message || err);
        } finally {
            setIsUploadingBg(false);
            if (customBgInputRef.current) customBgInputRef.current.value = '';
        }
    };

    const handlePrelaunchDeleteBg = async (bgId) => {
        setIsDeletingBgId(bgId);
        try {
            await deleteVirtualBackground(bgId);
            const deleted = roomBackgrounds.find((bg) => bg._id === bgId);
            setRoomBackgrounds((prev) => prev.filter((bg) => bg._id !== bgId));
            if (deleted && selectedBackground === deleted.url) {
                handleSelectPrelaunchBackground(null);
            }
        } catch (err) {
            console.warn("[VirtualBG] Delete failed:", err?.message || err);
        } finally {
            setIsDeletingBgId(null);
        }
    };

    const handleStartVirtualBackground = async () => {
        // Initialize processor if not ready
        if (!videoProcessor.ready) {
            await videoProcessor.init();
        }

        // Configuration for starting processor
        const config = {
            type: "image", // "blur"
            imageUrl: selectedBackground ? selectedBackground : "https://cdn.videosdk.live/virtual-background/cloud.jpeg",
            // Here is a list of background images you can use for your project.
            // imageUrl: "https://cdn.videosdk.live/virtual-background/beach.jpeg",
            // imageUrl: "https://cdn.videosdk.live/virtual-background/san-fran.jpeg",
            // imageUrl: "https://cdn.videosdk.live/virtual-background/paper-wall.jpeg",
        };

        // Getting stream from webcam
        const stream = await createCameraVideoTrack({});
        const processedStream = await videoProcessor.start(stream, config);

        if (webcamOn) {
            const currentvideoTrack = videoTrackRef.current;
            if (currentvideoTrack) {
                currentvideoTrack.stop();
            }

            const stream = processedStream;
            setCustomVideoStream(stream);
            const videoTracks = stream?.getVideoTracks();
            const videoTrack = videoTracks?.length ? videoTracks[0] : null;
            setVideoTrack(videoTrack);
        }
    };


    const handlePasswordInput = (index, value) => {
        const newPasswordDigits = [...passwordDigits];
        newPasswordDigits[index] = value;
        setPasswordDigits(newPasswordDigits);

        // Clear error when user starts typing
        if (passwordError) {
            setPasswordError(false);
        }

        if (value && index < 3) {
            passwordInputRefs.current[index + 1]?.focus();
        }

    };

    const handlePasswordKeyDown = (index, e) => {
        if (e.key === "Backspace") {
            if (!passwordDigits[index] && index > 0) {
                const newPasswordDigits = [...passwordDigits];
                newPasswordDigits[index - 1] = "";
                setPasswordDigits(newPasswordDigits);
                passwordInputRefs.current[index - 1]?.focus();
            } else if (passwordDigits[index]) {
                const newPasswordDigits = [...passwordDigits];
                newPasswordDigits[index] = "";
                setPasswordDigits(newPasswordDigits);
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            passwordInputRefs.current[index - 1]?.focus();
        } else if (e.key === "ArrowRight" && index < 3) {
            passwordInputRefs.current[index + 1]?.focus();
        }
    };

    const handlePasswordPaste = (e) => {
        const pastedText = e.clipboardData.getData("text");
        const newPasswordDigits = [...passwordDigits];
        for (let i = 0; i < pastedText.length; i++) {
            if (i < 4) {
                newPasswordDigits[i] = pastedText[i];
            }
        }
        setPasswordDigits(newPasswordDigits);

        const nextEmptyIndex = newPasswordDigits.findIndex((digit) => !digit);
        const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 3;
        setTimeout(() => {
            passwordInputRefs.current[focusIndex]?.focus();
        }, 0);
    };

    // Show waiting room screen if participant is waiting for admission
    if (isInWaitingRoom || wasRejected) {
        const participantId = user?.id || localStorage.getItem(meetingId);
        return (
            <WaitingForApproval
                onLeaveQueue={leaveWaitingRoom}
                isWaitingForApproval={isInWaitingRoom && !wasRejected}
                isRejected={wasRejected}
                rejectionReason={rejectionReason}
                hostIsOnline={hostIsOnline}
                socket={waitingRoomSocketRef.current}
                meetingId={meetingId}
                odId={participantId}
                odName={userName}
            />
        );
    }

    // Recorder mode: show branded loading screen while auto-joining
    if (isRecorderMode) {
        return (
            <div style={{
                width: '100vw', height: '100vh',
                background: 'linear-gradient(135deg, #060621 0%, #0a0a2e 50%, #060621 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                <img
                    src="https://studio.r-link.com/static/media/R-Link-Studio-Logo-white.98c5ad1f49984249942d.png"
                    alt="R-Link"
                    style={{ width: 300, opacity: 0.8 }}
                />
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen bg-[var(--bk-background,#001233)] text-white font-sans">
                {/* Background Effects */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
                </div>

                {/* Header Bar */}
                <div className="sticky top-0 z-20 px-6 py-4 bg-[var(--bk-background,#001233)]/80 backdrop-blur-sm border-b border-white/10 flex justify-between items-center">
                    <div className="flex items-center">
                        <RLinkLogo size="lg" companyLogo={wlBranding.companyLogo} companyName={wlBranding.companyName} />
                    </div>
                    {isAuthenticated && (
                        <button
                            onClick={() => navigate("/dashboard")}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg hover:opacity-90 text-white text-sm font-sans transition-opacity"
                            style={{ background: `linear-gradient(to right, ${bkPrimary}, ${bkAccent})` }}
                        >
                            Dashboard
                        </button>
                    )}
                </div>

                <div className="relative z-10 flex flex-col lg:flex-row min-h-[calc(100vh-65px)]">
                    {/* Left Panel - Video Preview */}
                    <div className="flex-1 flex items-center justify-center p-4 md:p-6 lg:p-8">
                        <div className="w-full max-w-2xl">
                            {/* Video Preview */}
                            <div className="relative aspect-video bg-gray-900 rounded-2xl overflow-hidden border border-white/10">
                                <video
                                    autoPlay
                                    playsInline
                                    muted
                                    ref={videoPlayerRef}
                                    controls={false}
                                    style={{
                                        transform: "scaleX(-1)",
                                        WebkitTransform: "scaleX(-1)",
                                    }}
                                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                                        webcamOn ? "opacity-100" : "opacity-0"
                                    }`}
                                />

                                {/* Camera Off Avatar */}
                                {!webcamOn && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                                        <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(to bottom right, ${bkPrimary}, ${bkAccent})` }}>
                                            <span className="text-3xl font-bold font-sans">
                                                {userName ? userName[0].toUpperCase() : "G"}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Controls Overlay */}
                                <div className="absolute bottom-3 md:bottom-4 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3">
                                    <button
                                        onClick={_toggleMic}
                                        disabled={
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                        }
                                        type="button"
                                        aria-label="Toggle microphone"
                                        className={`p-3 md:p-4 rounded-full transition-all ${
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                                ? "opacity-50 cursor-not-allowed bg-white/10"
                                                : micOn
                                                ? "bg-white/10 hover:bg-white/20"
                                                : "bg-red-500 hover:bg-red-600"
                                        }`}
                                    >
                                        {micOn ? (
                                            <Mic className="w-4 h-4 md:w-5 md:h-5" />
                                        ) : (
                                            <MicOff className="w-4 h-4 md:w-5 md:h-5" />
                                        )}
                                    </button>

                                    <button
                                        onClick={_toggleWebcam}
                                        disabled={
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                        }
                                        type="button"
                                        aria-label="Toggle camera"
                                        className={`p-3 md:p-4 rounded-full transition-all ${
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                                ? "opacity-50 cursor-not-allowed bg-white/10"
                                                : webcamOn
                                                ? "bg-white/10 hover:bg-white/20"
                                                : "bg-red-500 hover:bg-red-600"
                                        }`}
                                    >
                                        {webcamOn ? (
                                            <Video className="w-4 h-4 md:w-5 md:h-5" />
                                        ) : (
                                            <VideoOff className="w-4 h-4 md:w-5 md:h-5" />
                                        )}
                                    </button>
                                </div>

                                {/* Name Label */}
                                <div className="absolute bottom-3 md:bottom-4 left-3 md:left-4 bg-black/50 backdrop-blur-sm px-2.5 py-1 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-sans truncate max-w-[calc(50%-4.25rem)] md:max-w-[calc(50%-5.5rem)]">
                                    {userName || "Guest"}
                                </div>
                            </div>

                            {/* Virtual Background Button */}
                            {!(meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "speaker"].includes(meetingRole)) && (
                                <div className="mt-3 md:mt-4 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowVirtualBackgroundModal(true)}
                                        className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors font-sans"
                                    >
                                        <Sparkles className="w-4 h-4 text-purple-400" />
                                        {selectedBackground ? "Change Virtual Background" : "Virtual Background"}
                                    </button>
                                </div>
                            )}

                            {/* Audio Level Meter */}
                            <div className="mt-3 md:mt-4 flex items-center gap-2 md:gap-3">
                                <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                                <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div
                                        className="h-full transition-all duration-100"
                                        style={{ background: `linear-gradient(to right, ${bkPrimary}, ${bkAccent})` }}
                                        style={{ width: `${(volume || 0) * 16}%` }}
                                    />
                                </div>
                            </div>

                            {/* Device Dropdowns */}
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
                                {/* Camera Dropdown */}
                                <div className="relative">
                                    <button
                                        onClick={() => {
                                            setShowVideoOption(!showVideoOption);
                                            setShowAudioOption(false);
                                            setShowSpeakerOption(false);
                                        }}
                                        disabled={
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                        }
                                        type="button"
                                        className={`w-full flex items-center justify-between p-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white font-sans ${
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                                ? "opacity-50 cursor-not-allowed"
                                                : ""
                                        }`}
                                    >
                                        <span className="truncate flex items-center gap-2 min-w-0">
                                            <Video className="w-3 h-3 flex-shrink-0" />
                                            <span className="truncate">{selectedWebcam?.label || "Select camera"}</span>
                                        </span>
                                        <ChevronDown
                                            className={`w-3 h-3 flex-shrink-0 ml-1 transition-transform ${
                                                showVideoOption ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>
                                    {showVideoOption && webcams && webcams.length > 0 && (
                                        <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#1a1a2e] border border-white/10 rounded-lg overflow-hidden z-50 max-h-48 overflow-y-auto">
                                            {webcams.map((option, index) => (
                                                <button
                                                    key={option.deviceId || index}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedWebcam((s) => ({
                                                            ...s,
                                                            id: option?.deviceId,
                                                            label: option?.label,
                                                        }));
                                                        changeWebcam(option?.deviceId);
                                                        setShowVideoOption(false);
                                                    }}
                                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors font-sans ${
                                                        selectedWebcam?.label === option?.label
                                                            ? "bg-[var(--bk-primary,#6a1fbf)]/30"
                                                            : ""
                                                    }`}
                                                >
                                                    {option.label || option.deviceId}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Mic Dropdown */}
                                <div className="relative">
                                    <button
                                        onClick={() => {
                                            setShowAudioOption(!showAudioOption);
                                            setShowVideoOption(false);
                                            setShowSpeakerOption(false);
                                            if (!showAudioOption) {
                                                setShowMicTest(false);
                                            }
                                        }}
                                        disabled={
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                        }
                                        type="button"
                                        className={`w-full flex items-center justify-between p-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white font-sans ${
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                                ? "opacity-50 cursor-not-allowed"
                                                : ""
                                        }`}
                                    >
                                        <span className="truncate flex items-center gap-2 min-w-0">
                                            <Mic className="w-3 h-3 flex-shrink-0" />
                                            <span className="truncate">{selectedMic?.label || (mics.length === 0 ? "No microphone found" : "Select microphone")}</span>
                                        </span>
                                        <ChevronDown
                                            className={`w-3 h-3 flex-shrink-0 ml-1 transition-transform ${
                                                showAudioOption ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>
                                    {showAudioOption && mics && mics.length > 0 && (
                                        <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#1a1a2e] border border-white/10 rounded-lg overflow-hidden z-50 max-h-48 overflow-y-auto">
                                            {mics.map((option, index) => (
                                                <button
                                                    key={option.deviceId || index}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedMic((s) => ({
                                                            ...s,
                                                            label: option?.label,
                                                            id: option?.deviceId,
                                                        }));
                                                        changeMic(option?.deviceId);
                                                        setShowAudioOption(false);
                                                    }}
                                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors font-sans ${
                                                        option.label === selectedMic?.label
                                                            ? "bg-[var(--bk-primary,#6a1fbf)]/30"
                                                            : ""
                                                    }`}
                                                >
                                                    {option.label || option.deviceId}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Speaker Dropdown (Desktop Only) */}
                                {!isMobile && (
                                    <div className="relative">
                                        <button
                                            onClick={() => {
                                                setShowSpeakerOption(!showSpeakerOption);
                                                setShowVideoOption(false);
                                                setShowAudioOption(false);
                                            }}
                                            type="button"
                                            className="w-full flex items-center justify-between p-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white font-sans"
                                        >
                                            <span className="truncate flex items-center gap-2 min-w-0">
                                                <Volume2 className="w-3 h-3 flex-shrink-0" />
                                                <span className="truncate">{selectedSpeaker?.label || (speakers.length === 0 ? "No speaker found" : "Select speaker")}</span>
                                            </span>
                                            <ChevronDown
                                                className={`w-3 h-3 flex-shrink-0 ml-1 transition-transform ${
                                                    showSpeakerOption ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                        {showSpeakerOption && speakers && speakers.length > 0 && (
                                            <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#1a1a2e] border border-white/10 rounded-lg overflow-hidden z-50 max-h-48 overflow-y-auto">
                                                {speakers.map((option, index) => (
                                                    <button
                                                        key={option.deviceId || index}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedSpeaker((s) => ({
                                                                ...s,
                                                                id: option?.deviceId,
                                                                label: option?.label,
                                                            }));
                                                            setShowSpeakerOption(false);
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors font-sans ${
                                                            selectedSpeaker?.label === option?.label
                                                                ? "bg-[var(--bk-primary,#6a1fbf)]/30"
                                                                : ""
                                                        }`}
                                                    >
                                                        {option.label || option.deviceId}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Hidden Audio Elements */}
                            <audio
                                src="/audio/cartoon-door-bell.wav"
                                preload="auto"
                                className="hidden"
                            ></audio>
                            {audio && <audio src={audio} className="hidden"></audio>}
                        </div>
                    </div>

                    {/* Right Panel */}
                    <div className="lg:w-[500px] xl:w-[600px] bg-white/5 backdrop-blur-xl lg:border-l border-t lg:border-t-0 border-white/10 flex flex-col max-h-screen">
                        {/* Header - Fixed */}
                        <div className="p-4 md:p-6 border-b border-white/10">
                            <div className="flex items-center gap-2 md:gap-3">
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(to bottom right, ${bkPrimary}, ${bkAccent})` }}>
                                    <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
                                </div>
                                <div>
                                    <h2 className="text-sm md:text-base font-bold font-sans">
                                        Pre-Launch Check
                                    </h2>
                                    <p className="text-xs md:text-sm text-gray-400 font-sans">
                                        Configure your settings
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Scrollable Form */}
                        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
                            {/* Room Name Bar */}
                            <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 flex items-center justify-between">
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="text-gray-400 text-sm whitespace-nowrap">Room:</span>
                                    <span className="text-[#8B5CF6] font-medium text-sm truncate">
                                        {roomName}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {copiedRoomName && (
                                        <span className="text-xs text-[#8B5CF6]">Copied!</span>
                                    )}
                                    <button
                                        onClick={() => {
                                            if (!copiedRoomName) {
                                                copyRoomName();
                                            }
                                        }}
                                        type="button"
                                        title="Copy room URL"
                                        className="p-1 rounded-md hover:bg-white/10 text-[#8B5CF6] transition-colors"
                                    >
                                        {copiedRoomName ? (
                                            <Check className="w-4 h-4" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Your Name Input */}
                            <div>
                                <label className="block text-sm text-gray-400 mb-2 font-sans">
                                    Your Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter your name"
                                    required
                                    value={userName}
                                    onChange={(e) => {
                                        setUserName(e.target.value);
                                        if (nameError && e.target.value.trim() !== "") {
                                            setNameError(false);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!userName || userName.trim() === "") {
                                            setNameError(true);
                                        } else {
                                            setNameError(false);
                                        }
                                    }}
                                    className={`w-full p-3 bg-white/5 border rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none transition-colors font-sans ${
                                        nameError
                                            ? "border-red-500 focus:border-red-500"
                                            : "border-white/10 focus:border-[var(--bk-primary,#6a1fbf)]"
                                    }`}
                                />
                                {nameError && (
                                    <p className="text-red-500 text-xs mt-1 font-sans">
                                        Name is required
                                    </p>
                                )}
                            </div>

                            {/* Meeting/Webinar Name Input (Moderator Only) */}
                            {meetingRole === "moderator" && (
                                <div>
                                    <label className="block text-sm text-gray-400 mb-2 font-sans">
                                        {meetingSettings?.roomType === "Webinar" ? "Webinar Name" : "Meeting Name"}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={meetingSettings?.roomType === "Webinar" ? "Enter webinar name" : "Enter meeting name"}
                                        value={customMeetingName}
                                        onChange={(e) => setCustomMeetingName(e.target.value)}
                                        className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[var(--bk-primary,#6a1fbf)] transition-colors font-sans"
                                    />
                                </div>
                            )}

                            {/* Password Section */}
                            {meetingSettings?.requirePassword && (
                                <div>
                                    <label className="block text-sm text-gray-400 mb-2 font-sans">
                                        Room Password
                                    </label>
                                    <div className="flex gap-3 justify-center">
                                        {passwordDigits.map((digit, index) => (
                                            <input
                                                key={index}
                                                ref={(el) =>
                                                    (passwordInputRefs.current[index] = el)
                                                }
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                maxLength="1"
                                                value={digit}
                                                onChange={(e) =>
                                                    handlePasswordInput(index, e.target.value)
                                                }
                                                onKeyDown={(e) =>
                                                    handlePasswordKeyDown(index, e)
                                                }
                                                onPaste={handlePasswordPaste}
                                                className={`w-12 h-12 bg-white/5 text-white text-center text-xl font-semibold rounded-xl border-2 focus:outline-none transition-colors font-sans ${
                                                    passwordError
                                                        ? "border-red-500 focus:border-red-500"
                                                        : "border-white/10 focus:border-[var(--bk-accent,#00c853)]"
                                                }`}
                                            />
                                        ))}
                                    </div>
                                    {passwordError && (
                                        <p className="text-red-500 text-xs mt-2 text-center font-sans">
                                            Password is not correct
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Virtual Background - hidden for now */}
                            <div className="hidden">
                                <label className="block text-sm text-gray-400 mb-2 font-sans">
                                    Virtual Background
                                </label>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!showVirtualBackground) {
                                                setShowVirtualBackground(true);
                                                handleStartVirtualBackground();
                                            }
                                        }}
                                        disabled={
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                        }
                                        className={`px-4 py-2 text-xs rounded-lg transition-colors font-sans ${
                                            showVirtualBackground
                                                ? "bg-[var(--bk-primary,#6a1fbf)] text-white"
                                                : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"
                                        } ${
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                                ? "opacity-50 cursor-not-allowed"
                                                : ""
                                        }`}
                                    >
                                        Enable
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (showVirtualBackground) {
                                                setShowVirtualBackground(false);
                                                changeWebcam(selectedWebcam.id);
                                            }
                                        }}
                                        disabled={
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                        }
                                        className={`px-4 py-2 text-xs rounded-lg transition-colors font-sans ${
                                            !showVirtualBackground
                                                ? "bg-[var(--bk-primary,#6a1fbf)] text-white"
                                                : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"
                                        } ${
                                            meetingSettings?.roomType === "Webinar" &&
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                                ? "opacity-50 cursor-not-allowed"
                                                : ""
                                        }`}
                                    >
                                        Disable
                                    </button>
                                    {showVirtualBackground && (
                                        <button
                                            type="button"
                                            onClick={() => setShowVirtualBackgroundModal(true)}
                                            disabled={
                                                meetingSettings?.roomType === "Webinar" &&
                                                !["moderator", "co-host", "speaker"].includes(meetingRole)
                                            }
                                            className={`px-4 py-2 text-xs rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors font-sans ${
                                                meetingSettings?.roomType === "Webinar" &&
                                                !["moderator", "co-host", "speaker"].includes(meetingRole)
                                                    ? "opacity-50 cursor-not-allowed"
                                                    : ""
                                            }`}
                                        >
                                            Choose Background
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Role Selector */}
                            <div>
                                <label className="block text-sm text-gray-400 mb-2 font-sans">
                                    Join as:
                                </label>
                                <div className="flex items-center bg-white/5 border border-white/10 rounded-xl p-1">
                                    <button
                                        type="button"
                                        disabled
                                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 font-sans cursor-not-allowed ${
                                            ["moderator", "co-host", "speaker"].includes(meetingRole)
                                                ? "bg-[#8B5CF6] text-white shadow-lg"
                                                : "text-white/50"
                                        }`}
                                    >
                                        Moderator
                                    </button>
                                    <button
                                        type="button"
                                        disabled
                                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 font-sans cursor-not-allowed ${
                                            !["moderator", "co-host", "speaker"].includes(meetingRole)
                                                ? "bg-[#8B5CF6] text-white shadow-lg"
                                                : "text-white/50"
                                        }`}
                                    >
                                        Participant
                                    </button>
                                </div>
                            </div>

                            {/* Login prompt for non-authenticated users */}
                            {!isAuthenticated && (
                                <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center">
                                    <p className="text-sm text-gray-400 font-sans">
                                        If you want to join as Host,{" "}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                localStorage.setItem("roomId", getRoomUrlSlug(meetingSettings) || roomName);
                                                navigate("/login");
                                            }}
                                            className="text-[#8B5CF6] hover:text-[#a78bfa] underline font-medium transition-colors"
                                        >
                                            please login
                                        </button>
                                    </p>
                                </div>
                            )}

                            {/* Start Meeting Button - sits directly under settings */}
                            <button
                                onClick={() => handleLaunchRoom()}
                                aria-label="Join Room"
                                className="w-full py-3 text-sm font-semibold hover:opacity-90 rounded-xl transition-opacity font-sans"
                                style={{ background: `linear-gradient(to right, ${bkPrimary}, ${bkAccent})` }}
                            >
                                {["moderator", "co-host", "speaker"].includes(meetingRole)
                                    ? "Start Meeting"
                                    : "Join Meeting"}
                            </button>
                        </div>

                        {/* Footer - Fixed */}
                        <div className="p-4 md:p-6 border-t border-white/10">
                            {!wlBranding.isWhiteLabel && (
                                <div className="text-xs text-gray-500 text-center font-sans">
                                    <p>
                                        R-Link Email Support:{" "}
                                        <a
                                            href="mailto:support@r-link.com"
                                            className="text-[var(--bk-accent,#00c853)] hover:underline"
                                        >
                                            support@r-link.com
                                        </a>
                                    </p>
                                    <p className="mt-1">
                                        <a
                                            href="https://r-link.com/terms-of-services"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline hover:text-white"
                                        >
                                            Terms & Conditions
                                        </a>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Virtual Background Modal */}
            {showVirtualBackgroundModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowVirtualBackgroundModal(false)}
                    ></div>

                    <div className="relative bg-[#0a1929] border border-white/10 rounded-2xl shadow-xl w-full max-w-lg mx-4">
                        <div className="flex items-center justify-between p-4 border-b border-white/10">
                            <h3 className="text-lg font-semibold text-white font-sans flex items-center gap-2">
                                <ImageIcon className="w-5 h-5 text-purple-400" />
                                Virtual Backgrounds
                            </h3>
                            <button
                                onClick={() => setShowVirtualBackgroundModal(false)}
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4 max-h-[60vh] overflow-y-auto">
                            <span className="text-xs text-gray-400 block mb-2 font-sans">Select Background</span>
                            <div className="grid grid-cols-4 gap-2">
                                {/* No Background */}
                                <button
                                    type="button"
                                    onClick={() => handleSelectPrelaunchBackground(null)}
                                    title="No background"
                                    className={`aspect-video rounded-lg border-2 transition-all flex items-center justify-center bg-gray-800 ${
                                        !selectedBackground
                                            ? "border-[var(--bk-primary,#6a1fbf)] ring-2 ring-[var(--bk-primary,#6a1fbf)]/50"
                                            : "border-white/10 hover:border-white/30"
                                    }`}
                                >
                                    <X className="w-4 h-4 text-gray-400" />
                                </button>

                                {/* User uploaded backgrounds */}
                                {roomBackgrounds.map((bg) => (
                                    <div key={bg._id} className="relative group">
                                        <button
                                            type="button"
                                            onClick={() => handleSelectPrelaunchBackground(bg.url)}
                                            className={`w-full aspect-video rounded-lg border-2 transition-all overflow-hidden ${
                                                selectedBackground === bg.url
                                                    ? "border-[var(--bk-primary,#6a1fbf)] ring-2 ring-[var(--bk-primary,#6a1fbf)]/50"
                                                    : "border-white/10 hover:border-white/30"
                                            }`}
                                        >
                                            <img
                                                src={bg.url}
                                                alt={bg.originalName || "Custom"}
                                                className="w-full h-full object-cover"
                                                crossOrigin="anonymous"
                                            />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handlePrelaunchDeleteBg(bg._id);
                                            }}
                                            disabled={isDeletingBgId === bg._id}
                                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                            title="Delete background"
                                        >
                                            {isDeletingBgId === bg._id
                                                ? <Loader2 className="w-3 h-3 text-white animate-spin" />
                                                : <X className="w-3 h-3 text-white" />}
                                        </button>
                                    </div>
                                ))}

                                {/* Preset backgrounds */}
                                {backgroundImageUrls.map((bg, index) => (
                                    <button
                                        key={`preset-${index}`}
                                        type="button"
                                        onClick={() => handleSelectPrelaunchBackground(bg)}
                                        className={`aspect-video rounded-lg border-2 transition-all overflow-hidden ${
                                            selectedBackground === bg
                                                ? "border-[var(--bk-primary,#6a1fbf)] ring-2 ring-[var(--bk-primary,#6a1fbf)]/50"
                                                : "border-white/10 hover:border-white/30"
                                        }`}
                                    >
                                        <img
                                            src={bg}
                                            alt={`Background ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            crossOrigin="anonymous"
                                        />
                                    </button>
                                ))}
                            </div>

                            {/* Upload Custom */}
                            <div className="mt-4">
                                <input
                                    ref={customBgInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePrelaunchBgUpload}
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    onClick={() => customBgInputRef.current?.click()}
                                    disabled={isUploadingBg || !meetingSettings?.roomId}
                                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isUploadingBg ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4" />
                                            Upload Custom Background
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-3 border-t border-white/10">
                            <p className="text-xs text-gray-400 text-center font-sans">
                                {webcamOn
                                    ? "Your selection is applied to the preview and saved for the meeting."
                                    : "Turn on your camera to preview. Selection will be saved for the meeting."}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Duplicate Session Modal */}
            {showDuplicateSessionModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="relative bg-[#0a1929] border border-white/10 rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
                        {/* Warning Icon */}
                        <div className="flex justify-center mb-4">
                            <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center">
                                <svg className="w-7 h-7 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-white text-center mb-2 font-sans">
                            Already in this meeting
                        </h3>

                        {/* Message */}
                        <p className="text-sm text-gray-400 text-center mb-6 font-sans">
                        Your logged in account is already in this meeting on another device. Would you like to remove them from this meeting and take their place? (Note this will remove them from the meeting)
                        </p>

                        {/* Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={handleDuplicateSessionCancel}
                                className="flex-1 py-3 text-sm font-semibold text-white bg-white/5 border border-white/10 hover:bg-white/10 rounded-xl transition-colors font-sans"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDuplicateSessionConfirm}
                                className="flex-1 py-3 text-sm font-semibold text-white hover:opacity-90 rounded-xl transition-opacity font-sans"
                                style={{ background: `linear-gradient(to right, ${bkPrimary}, ${bkAccent})` }}
                            >
                                Join Here
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
